/*
 * Sketch to Slide — reads the selected sketch image shape's geometry and
 * captures its pixel data.
 *
 * Mechanics (Office.js selection read + slide-capture-and-crop) live in
 * shared/slideImageCapture.ts, shared with photoHeadSelection.ts. Only this
 * feature's message copy stays here.
 */

import type { SlideImageMessages, SlideImageReasons } from "../../shared/services/slideImageCapture";
import { captureSelectedImage, readSelectedImageShape } from "../../shared/services/slideImageCapture";
import { logSketchToSlideError } from "./sketchToSlideApi";
import type { SelectedSketchShape } from "../../../types";

export const PPT_ONLY_MESSAGE = "This tool works only in PowerPoint.";
export const NO_SELECTION_MESSAGE = "Select a sketch image on the slide first.";
export const MULTI_SELECTION_MESSAGE = "Select just one image — only one sketch can be converted at a time.";
export const NOT_AN_IMAGE_MESSAGE = "The selected shape isn't a picture. Select a sketch image instead.";
export const CAPTURE_UNSUPPORTED_MESSAGE =
  "Reading the selected sketch needs PowerPointApi 1.5+ — try PowerPoint on the web, or update desktop PowerPoint.";

const MESSAGES: SlideImageMessages = {
  pptOnly: PPT_ONLY_MESSAGE,
  noSelection: NO_SELECTION_MESSAGE,
  multiSelection: MULTI_SELECTION_MESSAGE,
  notAnImage: NOT_AN_IMAGE_MESSAGE,
  captureUnsupported: CAPTURE_UNSUPPORTED_MESSAGE,
  itemLabel: "sketch",
  strictCapture: true,
};

const REASONS: SlideImageReasons = {
  placeholderTypeCheck: "sketch-to-slide-placeholder-type-check",
  slideSizeRead: "sketch-to-slide-slide-size-read",
  slideCapture: "sketch-to-slide-slide-capture",
  cropEncode: "sketch-to-slide-crop-encode",
  cropEmptyResult: "sketch-to-slide-crop-empty-result",
};

/** Read the current selection: exactly one image/picture shape, with its geometry. */
export function readSelectedSketchShape(): Promise<SelectedSketchShape> {
  return readSelectedImageShape(MESSAGES, logSketchToSlideError, REASONS);
}

/** Capture the selected sketch image shape's pixels as a base64 PNG. */
export function captureSelectedSketchImage(shape: SelectedSketchShape): Promise<{ base64: string; mimeType: string }> {
  return captureSelectedImage(shape, MESSAGES, logSketchToSlideError, REASONS);
}
